import { supabase } from '@/lib/supabase'

/**
 * Upload a file to the `attachments` Storage bucket + insert an `attachments` row bound
 * to a specific event (or standalone). Returns the attachment row so callers can link it
 * from an event payload if needed.
 *
 * Path convention: `<family_id>/<yyyymm>/<uuid>-<ext>`
 *   The family-id prefix is enforced by storage.objects RLS.
 *   The yyyymm bucket keeps object list operations fast.
 */
export async function uploadAttachment(params: {
  familyId: string
  userId: string
  file: File
  eventId?: string
  documentId?: string
}): Promise<{
  id: string
  storage_ref: string
  mime_type: string | null
  byte_size: number
  signedUrl?: string
}> {
  const { familyId, userId, file, eventId, documentId } = params
  const ext = fileExt(file.name)
  const yyyymm = new Date().toISOString().slice(0, 7).replace('-', '')
  const uuid = crypto.randomUUID()
  const path = `${familyId}/${yyyymm}/${uuid}${ext}`

  // 1. Upload the object
  const { error: uploadErr } = await supabase.storage.from('attachments').upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  })
  if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`)

  // 2. Compute content hash for de-dup / integrity check (client-side SHA-256)
  const buf = await file.arrayBuffer()
  const digestBuf = await crypto.subtle.digest('SHA-256', buf)
  const contentSha = Array.from(new Uint8Array(digestBuf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

  // 3. Insert the attachments row
  const { data: row, error: rowErr } = await supabase
    .from('attachments')
    .insert({
      family_id: familyId,
      event_id: eventId ?? null,
      document_id: documentId ?? null,
      media_type: mediaTypeFromMime(file.type),
      storage_status: 'uploaded',
      storage_backend: 'supabase',
      storage_ref: path,
      content_sha256: contentSha,
      byte_size: file.size,
      mime_type: file.type || null,
      created_by_user_id: userId,
    })
    .select('id, storage_ref, mime_type, byte_size')
    .single()
  if (rowErr) {
    // Roll back the Storage object so we don't leak orphans
    await supabase.storage.from('attachments').remove([path])
    throw new Error(`Failed to record attachment: ${rowErr.message}`)
  }

  return {
    id: row!.id as string,
    storage_ref: row!.storage_ref as string,
    mime_type: (row!.mime_type as string | null) ?? null,
    byte_size: row!.byte_size as number,
  }
}

export async function signedAttachmentUrl(storageRef: string, ttlSeconds = 300): Promise<string | null> {
  const { data, error } = await supabase.storage.from('attachments').createSignedUrl(storageRef, ttlSeconds)
  if (error) return null
  return data.signedUrl ?? null
}

function fileExt(name: string): string {
  const m = name.match(/\.[a-z0-9]{2,5}$/i)
  return m ? m[0].toLowerCase() : ''
}

function mediaTypeFromMime(mime: string | undefined): string {
  if (!mime) return 'unknown'
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime.startsWith('audio/')) return 'audio'
  if (mime === 'application/pdf') return 'document'
  return 'other'
}

/**
 * Upload an avatar OR cover photo for a person. Uses the same `attachments`
 * bucket + RLS as event attachments, but writes the storage_ref directly to
 * `persons.avatar_storage_ref` or `persons.cover_storage_ref`. No attachments
 * row is created — the person row owns the reference.
 *
 * Old photo is deleted from Storage after the person row is patched, so no
 * orphans accumulate. Failures on the delete step are non-fatal (log + move on).
 */
export async function uploadPersonMedia(params: {
  familyId: string
  personId: string
  kind: 'avatar' | 'cover'
  file: File
}): Promise<{ storage_ref: string; signedUrl: string | null }> {
  const { familyId, personId, kind, file } = params

  // Path: <family_id>/persons/<person_id>/<kind>-<uuid><ext>
  const ext = fileExt(file.name)
  const uuid = crypto.randomUUID()
  const path = `${familyId}/persons/${personId}/${kind}-${uuid}${ext}`

  const { error: uploadErr } = await supabase.storage
    .from('attachments')
    .upload(path, file, {
      contentType: file.type || 'application/octet-stream',
      upsert: false,
    })
  if (uploadErr) throw new Error(`Upload failed: ${uploadErr.message}`)

  // Fetch the current ref so we can delete the old file after the swap succeeds
  const { data: existing } = await supabase
    .from('persons')
    .select(kind === 'avatar' ? 'avatar_storage_ref' : 'cover_storage_ref')
    .eq('id', personId)
    .maybeSingle()
  const previousRef = (existing as Record<string, string | null> | null)?.[
    kind === 'avatar' ? 'avatar_storage_ref' : 'cover_storage_ref'
  ] ?? null

  const patch = kind === 'avatar'
    ? { avatar_storage_ref: path }
    : { cover_storage_ref: path }
  const { error: updErr } = await supabase.from('persons').update(patch).eq('id', personId)
  if (updErr) {
    // Roll back the upload so no orphan sits in Storage
    await supabase.storage.from('attachments').remove([path])
    throw new Error(`Failed to save ${kind}: ${updErr.message}`)
  }

  // Best-effort delete of the old file. Ignore errors — Storage cleanup script
  // can catch stragglers later. What matters is the new photo is live now.
  if (previousRef && previousRef !== path) {
    supabase.storage.from('attachments').remove([previousRef]).catch(() => { /* noop */ })
  }

  const signedUrl = await signedAttachmentUrl(path, 3600)
  return { storage_ref: path, signedUrl }
}

/**
 * Clear a person's avatar OR cover. Deletes the Storage object + nulls the ref.
 */
export async function clearPersonMedia(params: {
  personId: string
  kind: 'avatar' | 'cover'
}): Promise<void> {
  const { personId, kind } = params
  const col = kind === 'avatar' ? 'avatar_storage_ref' : 'cover_storage_ref'
  const { data: row } = await supabase
    .from('persons')
    .select(col)
    .eq('id', personId)
    .maybeSingle()
  const ref = (row as Record<string, string | null> | null)?.[col] ?? null
  const { error } = await supabase.from('persons').update({ [col]: null }).eq('id', personId)
  if (error) throw new Error(`Failed to clear ${kind}: ${error.message}`)
  if (ref) {
    supabase.storage.from('attachments').remove([ref]).catch(() => { /* noop */ })
  }
}

/** Attach a client-side selected file to an already-created event via payload update. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
